import { useRef, useState } from 'react';
import {
  Box,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  faEllipsisH,
  faTrash,
  faUpload,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import {
  DeleteSheetVersionDocument,
  EventResource,
  EventType,
} from '../../gql/graphql';
import { Sheet, useSheets } from '../../hooks/sheets';
import ConfirmDeletionModal from '../common/ConfirmDeletionModal';
import SparkelIcon from '../common/icon/SparkelIcon';
import { DiffBadge } from './project-history/DiffBadge';
import { useDiffing } from './project-history/hooks/useDiffing';
import { useEnabledSheetViewer } from 'src/components/viewers/common/hooks/useEnabledSheetViewer';
import { useEventHistory } from 'src/hooks/event-history';

type SheetItemProps = {
  sheet: Sheet;
  readOnly: boolean;
  diffEventType?: EventType;
};

export default function SheetItem({
  sheet,
  readOnly,
  diffEventType,
}: SheetItemProps) {
  const { activeSheetId, changeActiveSheet } = useEnabledSheetViewer();
  const activeButtonColor = useColorModeValue('gray.300', 'gray.500');
  const isActiveSheet = activeSheetId !== null && activeSheetId === sheet.id;
  const { getTextColor, getBackgroundColor } = useDiffing();
  const diffTextColor = diffEventType ? getTextColor(diffEventType) : undefined;
  const diffBgColor = diffEventType
    ? getBackgroundColor(diffEventType)
    : undefined;
  const {
    isOpen: showDeleteSheetModal,
    onOpen: onDeleteSheetModalOpen,
    onClose: onDeleteSheetModalClose,
  } = useDisclosure();

  const toast = useToast();
  const { logEvent } = useEventHistory(sheet.projectId);

  const { t } = useTranslation(['project', 'common']);

  const projectId = sheet.projectId;
  const [, deleteSheetVersion] = useMutation(DeleteSheetVersionDocument);
  const { createSheet } = useSheets(projectId);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    isOpen: isConfirmOpen,
    onOpen: openConfirm,
    onClose: closeConfirm,
  } = useDisclosure();
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const handleUpload = async () => {
    inputRef.current?.click();
  };

  const handleUploadNewVersion = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target?.files?.[0] || null;
    if (!file) return;
    setPendingFile(file);
    openConfirm();
  };

  const confirmUploadNewVersion = async () => {
    if (!pendingFile) return;
    try {
      const nextVersion = (sheet.version ?? 1) + 1;
      const created = await createSheet(pendingFile, sheet.name, nextVersion);

      if (created?.id) {
        changeActiveSheet(created.id);
      }

      logEvent({
        eventName: 'Sheet Updated',
        eventType: EventType.Modified,
        resourceName: sheet.name,
        resourceType: EventResource.Sheet,
        metadata: {
          affectedResources: [sheet.id],
          details: `Sheet "${sheet.name}" updated to version ${nextVersion}`,
        },
      });

      toast({
        status: 'success',
        duration: 3000,
        title: t('project:toast.success.sheet-upload'),
      });
    } catch (err) {
      toast({
        status: 'error',
        duration: 3000,
        title: t('project:toast.error.sheet-upload'),
      });
    } finally {
      setPendingFile(null);
      closeConfirm();
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDeleteSheet = async () => {
    const { error } = await deleteSheetVersion({
      projectId,
      versionName: sheet.name,
    });

    if (error) {
      toast({
        title: t('toast.error.sheet-delete'),
        status: 'error',
      });
    } else {
      logEvent({
        eventName: 'Sheet Deleted',
        eventType: EventType.Deleted,
        resourceName: sheet.name,
        resourceType: EventResource.Sheet,
        metadata: {
          affectedResources: [sheet.id],
          details: `Sheet "${sheet.name}" deleted`,
        },
      });

      toast({
        title: t('toast.success.sheet-delete'),
        status: 'success',
      });
    }
  };

  return (
    <>
      <Flex
        id={sheet.id}
        aria-checked={isActiveSheet}
        paddingY={0.5}
        paddingX={2}
        onClick={() => changeActiveSheet(sheet.id)}
        cursor={'pointer'}
        role="group"
        _hover={{
          bgColor: 'gray.50',
          borderRadius: 'lg',
          _dark: {
            bgColor: 'gray.700',
          },
        }}
        _checked={{
          borderRadius: 'lg',
          bgColor: 'blue.50',
          textColor: 'blue.700',
          fontWeight: 'bold',

          _dark: {
            bgColor: 'blue.800',
            textColor: 'blue.200',
          },
        }}
        backgroundColor={isActiveSheet ? undefined : diffBgColor}
        color={isActiveSheet ? undefined : diffTextColor}
      >
        <Flex overflow="hidden" justifyContent="flex-start" alignItems="center">
          <Flex textAlign="left" flexGrow={1} py={1} my={-1} fontSize="sm">
            {(() => {
              const displayName =
                sheet.name && sheet.name !== sheet.id
                  ? sheet.name
                  : sheet.filename || sheet.id;
              return (
                <Tooltip label={displayName} openDelay={400}>
                  <Text noOfLines={1} wordBreak={'break-all'} maxWidth={'100%'}>
                    {displayName}
                  </Text>
                </Tooltip>
              );
            })()}
          </Flex>
        </Flex>

        {!readOnly ? (
          <Menu closeOnSelect={false} isLazy>
            <MenuButton
              opacity={0}
              _groupHover={{
                opacity: 1,
                transition: 'opacity 0.5s ease-in',
              }}
              _active={{ opacity: 1, background: activeButtonColor }}
              ml={'auto'}
              isRound
              as={IconButton}
              colorScheme="gray"
              size={'xs'}
              maxWidth={16}
              mr={1}
              onClick={(e) => e.stopPropagation()}
            >
              <SparkelIcon icon={faEllipsisH} />
            </MenuButton>

            <Portal>
              <MenuList maxW="xs" zIndex={99}>
                <MenuItem
                  icon={<SparkelIcon icon={faUpload} fixedWidth />}
                  onClick={async (e) => {
                    e.stopPropagation();
                    await handleUpload();
                  }}
                >
                  {t('files.sheets.upload-new-version')}
                  <input
                    ref={inputRef}
                    className="d-none"
                    type="file"
                    accept="application/pdf"
                    onChange={handleUploadNewVersion}
                    style={{ display: 'none' }}
                  />
                </MenuItem>
                <MenuItem
                  icon={
                    <SparkelIcon icon={faTrash} color={'red.400'} fixedWidth />
                  }
                  textColor={'red.400'}
                  onClick={async (e) => {
                    e.stopPropagation();
                    onDeleteSheetModalOpen();
                  }}
                >
                  {t('files.sheets.delete')}
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        ) : null}
        {diffEventType ? (
          <Box ml="auto" marginY="auto">
            <DiffBadge eventType={diffEventType} />
          </Box>
        ) : null}
      </Flex>

      <Modal
        isOpen={isConfirmOpen}
        onClose={() => {
          setPendingFile(null);
          closeConfirm();
          if (inputRef.current) inputRef.current.value = '';
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t('project:files.sheets.update-confirmation.title')}
          </ModalHeader>
          <ModalBody>
            {t('project:files.sheets.update-confirmation.message')}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => {
                setPendingFile(null);
                closeConfirm();
                if (inputRef.current) inputRef.current.value = '';
              }}
            >
              {t('common:cancel', 'Cancel')}
            </Button>
            <Button colorScheme="brand" onClick={confirmUploadNewVersion}>
              {t('common:confirm')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmDeletionModal
        onConfirm={handleDeleteSheet}
        isOpen={showDeleteSheetModal}
        onClose={onDeleteSheetModalClose}
      >
        <Box>{t('files.sheets.delete-confirmation.title')}</Box>
        <Box>{t('files.sheets.delete-confirmation.message')}</Box>
      </ConfirmDeletionModal>
    </>
  );
}
